import React, { useEffect, useMemo } from "react";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Box from "@mui/material/Box";
import { SxProps, Theme } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { useApolloClient, useQuery } from "@apollo/client";
import { useAccountContext } from "../context/AccountContext/useAccountContext";
import { useUserProfile } from "../hooks/useUserProfile";
import {
  GET_AVAILABLE_ACCOUNT_CONTEXTS,
  GetAvailableAccountContextsData,
} from "common/graphql/queries/GetAvailableAccountContexts";

export interface AccountContextDropdownProps {
  /**
   * When provided, the Select is wrapped in a `<Box sx={containerSx}>`.
   * Use this to apply separator borders and colour overrides in contexts like
   * the mobile drawer without the caller needing to know whether the dropdown
   * rendered at all — it still returns null when there is ≤ 1 context available.
   */
  containerSx?: SxProps<Theme>;
}

/**
 * A dropdown that lets the user switch between available account contexts
 * (parent account + child accounts). Renders null when there is only one
 * context available, or while data is still loading.
 *
 * **Query ownership:** this component runs `AvailableAccountContexts` directly.
 * {@link AccountContextInitializer} fires the same query earlier in the tree, so
 * Apollo serves the result from cache — no additional network request is made here.
 *
 * Trigger label:
 * - No context selected (operating as parent): shows the root account's display name.
 * - Context selected: shows the active child account's display name.
 */
export const AccountContextDropdown: React.FC<AccountContextDropdownProps> = ({ containerSx }) => {
  const { contextId, setContextId } = useAccountContext();
  const { userProfile, loading: profileLoading } = useUserProfile();
  const { data, loading: queryLoading } = useQuery<GetAvailableAccountContextsData>(GET_AVAILABLE_ACCOUNT_CONTEXTS);
  const navigate = useNavigate();
  const client = useApolloClient();

  const availableContexts = useMemo(() => data?.availableAccountContexts ?? [], [data]);

  // Secondary validation guard: if for any reason the dropdown mounts with a
  // contextId not present in the list (AccountContextInitializer handles the
  // primary case), clear it silently.
  useEffect(() => {
    if (queryLoading || !data || contextId === null) {
      return;
    }
    const isValid = availableContexts.some((ctx) => ctx.id === contextId);
    if (!isValid) {
      setContextId(null);
    }
  }, [data, queryLoading, contextId, availableContexts, setContextId]);

  if (queryLoading || profileLoading || !userProfile || availableContexts.length <= 1) {
    return null;
  }

  const rootId = userProfile.account.id;
  const selectValue = contextId ?? rootId;

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const selectedId = event.target.value;
    setContextId(selectedId === rootId ? null : selectedId);
    await client.clearStore();
    navigate("/");
  };

  const select = (
    <Select value={selectValue} onChange={handleChange} inputProps={{ "aria-label": "Account context" }}>
      {availableContexts.map((ctx) => (
        <MenuItem key={ctx.id} value={ctx.id}>
          {ctx.displayName}
        </MenuItem>
      ))}
    </Select>
  );

  if (containerSx) {
    return <Box sx={containerSx}>{select}</Box>;
  }

  return select;
};

export default AccountContextDropdown;
